import { StateCreator } from "zustand";
import { ReportDetailState } from "../interface";
import { getGlobalForceSave } from "../autoSaveGlobal";

const AUTO_SAVE_DELAY = 5000;

export interface AutoSaveActions {
  scheduleAutoSave: () => void;
  cancelAutoSave: () => void;
  isPendingSave: () => boolean;
  hasPendingChanges: () => boolean;
  setAutoSaveMutation: (mutation: any) => void;
  setAutoSavePending: (isPending: boolean) => void;
  setAutoSaveEnabled: (enabled: boolean) => void;
  clearPendingChanges: () => void;
}

export const createAutoSaveActions: StateCreator<
  ReportDetailState & { actions: any },
  [],
  [],
  AutoSaveActions
> = (set, get) => ({
  scheduleAutoSave: () => {
    set((state) => ({
      autoSave: {
        ...state.autoSave,
        hasPendingChanges: true,
      },
    }));

    // Lido DEPOIS do set acima, não antes — precisa refletir o
    // hasPendingChanges que acabou de ser gravado (e qualquer isPending já
    // em andamento) pra decisão abaixo ser sobre o estado atual, não uma
    // cópia presa no início da chamada.
    const state = get();

    if (!state.autoSave.isEnabled) {
      return;
    }

    // Um save já em voo: NÃO agenda outro em paralelo. `mutation.reset()`
    // só limpa o estado local do hook — não cancela a requisição HTTP já
    // em trânsito — então um segundo PUT concorrente podia ter sua
    // resposta processada pelo backend antes da primeira, e a primeira
    // (com um payload mais antigo, sem a edição mais recente) sobrescrevia
    // o resultado do segundo ao chegar depois. Isso é exatamente o quadro
    // relatado: apagar a seção 2 logo após a seção 1 mostrava sucesso mas
    // só a seção 1 ficava deletada. Em vez de tentar (mal) cancelar,
    // deixamos hasPendingChanges=true e o onSuccess/onError do save em
    // voo (useReportActionsWithAutoSave.tsx) reagenda assim que ele
    // terminar — nunca duas PUTs concorrentes.
    if (state.autoSave.isPending) {
      return;
    }

    // Limpa timeout anterior
    if (state.autoSave.timeoutId) {
      clearTimeout(state.autoSave.timeoutId);
    }

    // Agenda novo salvamento
    const timeoutId = setTimeout(() => {
      const forceSave = getGlobalForceSave();
      if (forceSave) {
        forceSave();
      }
    }, AUTO_SAVE_DELAY);

    set((state) => ({
      autoSave: {
        ...state.autoSave,
        timeoutId,
      },
    }));
  },

  cancelAutoSave: () => {
    const state = get();
    if (state.autoSave.timeoutId) {
      clearTimeout(state.autoSave.timeoutId);
    }
    set((state) => ({
      autoSave: {
        ...state.autoSave,
        timeoutId: null,
        hasPendingChanges: false,
      },
    }));
  },

  isPendingSave: () => get().autoSave.isPending,

  hasPendingChanges: () => get().autoSave.hasPendingChanges,

  setAutoSaveMutation: (mutation) =>
    set((state) => ({
      autoSave: { ...state.autoSave, mutation },
    })),

  setAutoSavePending: (isPending) =>
    set((state) => ({
      autoSave: { ...state.autoSave, isPending },
    })),

  setAutoSaveEnabled: (enabled) => {
    // Persistir no localstora para manter o estado entre refreshes
    try {
      localStorage.setItem("snap-reports:autosave-enabled", String(enabled));
    } catch (error) {
      console.warn("[Autosave] Failed to save preference:", error);
    }

    set((state) => ({
      autoSave: { ...state.autoSave, isEnabled: enabled },
    }));
  },

  clearPendingChanges: () =>
    set((state) => ({
      autoSave: {
        ...state.autoSave,
        hasPendingChanges: false,
      },
    })),
});
